import { useEffect, useRef, useState } from "react";
import { api, type ClassBudget, type LiveClass, type LiveStudent, lessonLabel } from "./api";
import { LevelUpChip } from "./LevelUpChip";
import { Alert, Back } from "./icons";
import { LiveProject } from "./LiveProject";
import { Lion } from "./Lion";
import { Avatar } from "./Sprite";
import "./grading.css"; // the teacher page bar (.gbar)
import "./LiveView.css";

// The teacher's live view (decision 68, Q; PRD §5.3 "Live grid"): one tile per student with what
// they're working on, how recently they did something, whether the helper is running for them,
// their last message and open flags, and helper messages used today with "+10 today" (FR-28). The
// class's helper budget this month turns into an alert at 80%. Refreshes every 5 s. "Pause class"
// stops new helper turns and puts the teacher's message on every student's screen until "Resume".

const POLL_MS = 5_000;
const DEFAULT_MESSAGE = "Eyes up front!";

const usd = (n: number) => `$${n.toFixed(2)}`;

const STATUS_LABEL: Record<LiveStudent["status"], string> = { working: "Working now", idle: "Idle 2+ min", away: "Away 10+ min" };

const ago = (t: number, now: number) => {
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  return new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

/** Home (teacher tools): opens the live view. P's Classes card can open it too (onOpen with a class id). */
export function LiveViewEntry({ onOpen }: { onOpen: (classId: string) => void }) {
  const [classes, setClasses] = useState<LiveClass[] | null>(null);
  useEffect(() => {
    void api.liveClasses().then((r) => setClasses(r.classes), () => setClasses(null));
  }, []);
  if (!classes) return null;
  const paused = classes.filter((c) => c.paused);
  return (
    <section aria-label="Live view" className="subs-entry live-entry">
      <div>
        <h2>Live view</h2>
        <p className="muted small">
          {classes.length === 0
            ? "No classes yet. Once you make a class and students join, you'll see each one working here and can pause the class."
            : paused.length
              ? `Paused now: ${paused.map((c) => c.name).join(", ")}`
              : "See what each student is doing, open their project, and pause the class."}
        </p>
      </div>
      {classes.length > 0 && (
        <div className="row">
          {classes.map((c) => (
            <button key={c.id} type="button" className="primary" onClick={() => onOpen(c.id)}>
              {classes.length === 1 ? "Live view" : `Live: ${c.name}${c.kind === "demo" ? " (demo)" : ""}`}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export function LiveView({ classId, onExit, onClass }: { classId: string | null; onExit: () => void; onClass: (id: string) => void }) {
  const [classes, setClasses] = useState<LiveClass[] | null>(null);
  const [data, setData] = useState<{ class: LiveClass; students: LiveStudent[]; budget: ClassBudget; now: number } | null>(null);
  const [granting, setGranting] = useState<string | null>(null); // a student getting +10 helper messages
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null); // a student's project, read-only
  const loaded = useRef(false);

  useEffect(() => {
    api.liveClasses().then(
      (r) => {
        setClasses(r.classes);
        if (!classId && r.classes[0]) onClass(r.classes[0].id);
      },
      (e: Error) => setError(e.message),
    );
  }, []);

  useEffect(() => {
    if (!classId) return;
    let alive = true;
    const load = () => {
      if (document.visibilityState !== "visible" && loaded.current) return; // no polling from a background tab
      api.live(classId).then(
        (r) => {
          if (!alive) return;
          loaded.current = true;
          setData(r);
          setError(null);
        },
        (e: Error) => alive && setError(e.message),
      );
    };
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [classId]);

  const cls = data?.class.id === classId ? data.class : null;
  const togglePause = async () => {
    if (!cls) return;
    setBusy(true);
    try {
      const r = cls.paused ? await api.resumeClass(cls.id) : await api.pauseClass(cls.id, message.trim() || DEFAULT_MESSAGE);
      setData((d) => (d ? { ...d, class: r.class } : d));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // "+10 today": more helper messages for one student, for today only.
  const grant = async (s: LiveStudent) => {
    if (!cls) return;
    setGranting(s.userId);
    try {
      const r = await api.extraTurns(cls.id, s.userId);
      setData((d) => (d ? { ...d, students: d.students.map((x) => (x.userId === s.userId ? { ...x, ...r } : x)) } : d));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGranting(null);
    }
  };

  if (open) return <LiveProject projectId={open} onBack={() => setOpen(null)} />;

  const students = data?.class.id === classId ? data.students : [];
  const count = (s: LiveStudent["status"]) => students.filter((x) => x.status === s).length;
  const running = students.filter((s) => s.helperRunning).length;

  return (
    <div className={`live-page${cls?.paused ? " is-paused" : ""}`}>
      <header className="gbar">
        <button type="button" className="pill-dark back" onClick={onExit}>
          <Back />
          Home
        </button>
        <div className="title">
          <span className="eyebrow gold">BuildLab · Teacher</span>
          <h1>
            Live view{cls ? ` · ${cls.name}` : ""}
            {cls?.kind === "demo" && <span className="badge-demo">Demo</span>}
          </h1>
        </div>
        {classes && classes.length > 1 && (
          <label className="live-pick">
            <span className="sr-only">Class</span>
            <select value={classId ?? ""} onChange={(e) => onClass(e.target.value)}>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.kind === "demo" ? " (demo)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {error && <p className="page-error">{error}</p>}
      {classes && classes.length === 0 && (
        <div className="live-empty">
          <Lion pose="think" size={104} />
          <p>No classes yet. Make a class on Home, put its join code on the projector, and students show up here as they join.</p>
        </div>
      )}

      {cls && (
        <section className={`live-pause${cls.paused ? " on" : ""}`} aria-label="Pause class">
          {cls.paused ? (
            <>
              <div className="lp-text">
                <span className="eyebrow">Class paused · the helper won't take new messages</span>
                <p>
                  Students see: <b>“{cls.pauseMessage}”</b>
                </p>
              </div>
              <button type="button" className="lp-button resume" disabled={busy} onClick={() => void togglePause()}>
                Resume
              </button>
            </>
          ) : (
            <>
              <label className="lp-text">
                <span className="eyebrow">Message on every student's screen</span>
                <input id="pause-message-input" value={message} maxLength={120} onChange={(e) => setMessage(e.target.value)} />
              </label>
              <button type="button" className="lp-button" disabled={busy} onClick={() => void togglePause()}>
                Pause class
              </button>
            </>
          )}
        </section>
      )}

      {cls && (
        <p className="live-summary" aria-live="polite">
          <span className="dot working" aria-hidden="true" /> {count("working")} working
          <span className="dot idle" aria-hidden="true" /> {count("idle")} idle
          <span className="dot away" aria-hidden="true" /> {count("away")} away
          {running > 0 && <span className="live-helper">Helper running for {running}</span>}
          <span className="muted small">Updates every 5 seconds</span>
        </p>
      )}

      {cls && data?.budget && (
        <p className={`live-budget${data.budget.alert ? " alert" : ""}`} role={data.budget.alert ? "alert" : undefined}>
          {data.budget.alert && <Alert size={16} stroke="#B5452F" width={2.5} />}
          <span>
            Helper budget this month: <b>{usd(data.budget.spentUsd)}</b> of {usd(data.budget.capUsd)}
            {data.budget.alert && (data.budget.spentUsd >= data.budget.capUsd ? ". It's used up: the helper is off for this class until next month." : `. That's ${Math.floor((data.budget.spentUsd / data.budget.capUsd) * 100)}% of it.`)}
          </span>
          <span className="lb-bar" aria-hidden="true">
            <span style={{ width: `${Math.min(100, (data.budget.spentUsd / data.budget.capUsd) * 100)}%` }} />
          </span>
        </p>
      )}

      {cls && students.length === 0 && (
        <div className="live-empty">
          <Lion pose="wave" size={104} />
          <p>No students in {cls.name} yet. They show up here as they join with the class code.</p>
        </div>
      )}

      {students.length > 0 && data && (
        <ul className="live-grid" aria-label="Students">
          {students.map((s) => (
            <li key={s.userId} className={s.screenedFlags > 0 ? "is-flagged" : undefined}>
              <button
                type="button"
                className={`live-tile st-${s.status}`}
                disabled={!s.project}
                onClick={() => s.project && setOpen(s.project.id)}
                aria-label={`${s.name}${s.screenedFlags > 0 ? " (flagged)" : ""}: ${STATUS_LABEL[s.status]}${s.project ? `, ${s.project.title}. Open their project.` : ", no project open yet."}`}
              >
                <span className="lt-head">
                  <Avatar name={s.name} avatar={s.avatar} size={32} className="lt-avatar" />
                  <span className="lt-name">{s.name}</span>
                  {s.screenedFlags > 0 && (
                    <span className="lt-flagged" title="Screening flagged a message to the helper (see Flagged for you on Home)">
                      Flagged
                    </span>
                  )}
                  {s.openFlags > 0 && (
                    <span className="lt-flags" title="Open flags (see Flagged for you on Home)">
                      <Alert size={12} stroke="#fff" width={2.5} />
                      {s.openFlags}
                    </span>
                  )}
                </span>
                <span className="lt-status">
                  <span className={`dot ${s.status}`} aria-hidden="true" />
                  {STATUS_LABEL[s.status]}
                  {s.lastSeenAt && <span className="muted"> · {ago(s.lastSeenAt, data.now)}</span>}
                </span>
                <span className="lt-project">
                  {s.project ? (
                    <>
                      {s.project.lesson !== null && <span className="eyebrow">{lessonLabel(s.project.lesson)}</span>}
                      <b>{s.project.title}</b>
                      {s.project.levelUps && <LevelUpChip count={s.project.levelUps} />}
                    </>
                  ) : (
                    <span className="muted">No project open yet</span>
                  )}
                </span>
                {s.helperRunning && <span className="lt-running">Helper is working…</span>}
                {s.lastMessage ? (
                  <span className="lt-last">
                    <span className={`lt-mode m-${s.lastMessage.mode}`}>{s.lastMessage.mode}</span>
                    <q>{s.lastMessage.text}</q>
                    <span className="muted small">{ago(s.lastMessage.at, data.now)}</span>
                  </span>
                ) : (
                  <span className="lt-last muted small">No messages to the helper yet</span>
                )}
              </button>
              <div className={`lt-usage${s.turnsToday >= s.turnLimit ? " out" : ""}`}>
                <span>
                  Helper today: <b>{s.turnsToday}</b> of {s.turnLimit}
                </span>
                <button type="button" className="outline small" disabled={granting === s.userId} onClick={() => void grant(s)} aria-label={`Give ${s.name} 10 more helper messages today`}>
                  +10 today
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
